import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Select } from "antd";
import { COUNTRY_CODES } from "./countryCodes";
import styles from "./SurveyWidgetPanel.module.scss";

const OTHER_CITY = "__other__";

// Fetched once per country per page load; public/geo/README.md says where the lists come from.
const cityCache = {};

export function loadCities(country) {
  if (!cityCache[country]) {
    cityCache[country] = fetch(`${process.env.PUBLIC_URL || ""}/geo/cities/${country}.json`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => (Array.isArray(list) ? list : []))
      .catch(() => {
        delete cityCache[country];
        return [];
      });
  }
  return cityCache[country];
}

/** Countries named in the survey language, sorted by that name. */
export function countryOptions(language) {
  let names = null;
  try {
    names = new Intl.DisplayNames([language || "en", "en"], { type: "region" });
  } catch (_) {
    names = null;
  }
  return COUNTRY_CODES.map((code) => ({ value: code, label: (names && names.of(code)) || code })).sort(
    (a, b) => a.label.localeCompare(b.label, language || "en")
  );
}

// Accent- and case-insensitive, so "turk" finds "Türkiye" and "izmir" finds "İzmir".
const searchKey = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/ı/g, "i");

export const matchesSearch = (input, option) =>
  option.value === OTHER_CITY || searchKey(option.label).includes(searchKey(input));

/**
 * "Country and city": a searchable country list, then the cities of that country (GeoNames, biggest
 * first) with "Other…" for a city that isn't listed. The value is { country: "TR", city: "İzmir" }.
 */
const LocationAnswer = ({ value, onChange = () => {}, language, labelledBy, required, invalid }) => {
  const { t } = useTranslation();
  const country = value?.country || "";
  const city = value?.city || "";
  const [cities, setCities] = useState(null);
  const [typing, setTyping] = useState(false);
  const countries = useMemo(() => countryOptions(language), [language]);

  useEffect(() => {
    let cancelled = false;
    setCities(null);
    if (!country) return undefined;
    loadCities(country).then((list) => {
      if (!cancelled) setCities(list);
    });
    return () => {
      cancelled = true;
    };
  }, [country]);

  // A city that isn't in the list (typed under "Other…", or no list for the country) is edited as text.
  const freeText = typing || (cities !== null && (cities.length === 0 || (city && !cities.includes(city))));
  const cityOptions = useMemo(
    () => [
      ...(cities || []).map((name) => ({ value: name, label: name })),
      { value: OTHER_CITY, label: t("surveyManagement.widget.city_other") },
    ],
    [cities, t]
  );

  return (
    <div className={styles.locationFields}>
      <Select
        showSearch
        value={country || undefined}
        placeholder={t("surveyManagement.widget.country_placeholder")}
        options={countries}
        filterOption={matchesSearch}
        popupClassName={styles.locationDropdown}
        onChange={(code) => {
          setTyping(false);
          onChange({ country: code, city: "" });
        }}
        aria-labelledby={labelledBy}
        aria-required={required || undefined}
        status={invalid && !country ? "error" : undefined}
      />
      {country && !freeText ? (
        <Select
          showSearch
          value={city || undefined}
          loading={cities === null}
          placeholder={t("surveyManagement.widget.city_placeholder")}
          options={cityOptions}
          filterOption={matchesSearch}
          popupClassName={styles.locationDropdown}
          onChange={(name) => {
            if (name === OTHER_CITY) {
              setTyping(true);
              onChange({ country, city: "" });
            } else {
              onChange({ country, city: name });
            }
          }}
          aria-label={t("surveyManagement.widget.city_placeholder")}
          status={invalid && !city ? "error" : undefined}
        />
      ) : null}
      {country && freeText ? (
        <Input
          value={city}
          maxLength={120}
          autoFocus={typing}
          placeholder={t("surveyManagement.widget.city_other_placeholder")}
          onChange={(e) => onChange({ country, city: e.target.value })}
          aria-label={t("surveyManagement.widget.city_placeholder")}
          status={invalid && !city.trim() ? "error" : undefined}
        />
      ) : null}
    </div>
  );
};

export default LocationAnswer;
